import React, { useContext, useMemo, useState } from 'react';

import { Snackbar, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { ExcalidrawController } from './components/ExcalidrawController';
import { WhiteboardContext } from './context/WhiteboardContext';
import { WhiteboardSessionContext } from './context/WhiteboardSessionContext';
import { SettingsDialog } from './SettingsDialog';
import { PageActions } from './state/WhiteboardSessionState';
import { Session, SessionTypes } from './types';
import { checkPagePermission } from './util/misc';
import { ConfirmationDialog } from '../common/dialog/ConfirmationDialog';

export interface WhiteboardContentProps {}

const WhiteboardContent: React.FC<WhiteboardContentProps> = () => {
    const { saveSession, userInfo } = useContext(WhiteboardContext);
    const {
        socketState,
        whiteboardSessionState,
        dispatchWhiteboardSessionAction,
        isInControl,
        setShowSettingsDialog,
        showSettingsDialog
    } = useContext(WhiteboardSessionContext);
    const { presenter, currentPage, session, notFound } = whiteboardSessionState;

    const [savingSession, setSavingSession] = useState(false);

    const { t } = useTranslation(['whiteboard', 'common']);

    const handleConfirmNotFound = () => {
        dispatchWhiteboardSessionAction({ type: PageActions.AcknowledgeNotFound });
    };

    const handleSessionSave = async (updatedSession: Session) => {
        setSavingSession(true);
        await saveSession(updatedSession);

        setShowSettingsDialog(false);
        setSavingSession(false);
    };

    // User can draw if they have permission to edit page AND (no one is presenting OR user is presenting).
    // On presentation mode I want students to be able to annotate while the instructor is presenting, if they turn on student annotations, so I'm going to change this for that mode
    const canEdit = useMemo(() => {
        // If page is lock no one can edit including instructors.
        if (!currentPage || currentPage.is_locked) {
            return false;
        }

        if (userInfo && currentPage && session?.type) {
            return (
                checkPagePermission(currentPage, userInfo, session.type) &&
                (session.type === SessionTypes.Presentation ||
                    !presenter ||
                    presenter.student_id === userInfo.student_id ||
                    session.type === SessionTypes.Individual)
            );
        }
        return false;
    }, [presenter, userInfo, currentPage, session]);

    if (!currentPage || !session || !userInfo) {
        return <></>;
    }

    return (
        <>
            <ConfirmationDialog
                title={t('pageNotFound')}
                open={notFound}
                onConfirm={handleConfirmNotFound}
                onClose={handleConfirmNotFound}
                cancelable={false}
            >
                <Typography>{t('pageNotFoundMessage')}</Typography>
            </ConfirmationDialog>
            <SettingsDialog
                open={showSettingsDialog}
                session={session}
                onClose={() => setShowSettingsDialog(false)}
                onSave={handleSessionSave}
                saving={savingSession}
            />
            <ExcalidrawController key={currentPage.page_id} readonly={!canEdit} isInControl={isInControl} />
            <Snackbar
                open={socketState.connectionStatus === 'connecting'}
                message="Connecting..."
                autoHideDuration={2000}
                anchorOrigin={{ horizontal: 'center', vertical: 'top' }}
            />
        </>
    );
};

export default WhiteboardContent;
